import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_ALL_USERS } from '../../apollo/queries';
import { useAuth } from '../AuthContext';
import '../css/CustomerAdminPage.css';

const CustomerAdminPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('all'); // all | email | name | mobile | phone | company
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedLevels, setSelectedLevels] = useState({
    all: true,
    A_GRADE: false,
    B_GRADE: false,
    C_GRADE: false,
    D_GRADE: false
  });

  // 관리자 권한 확인 해제 - 모든 사용자 접근 허용
  // React.useEffect(() => {
  //   if (!isAdmin()) {
  //     navigate('/');
  //   }
  // }, [isAdmin, navigate]);

  // GraphQL 쿼리 실행
  const { data, loading, error } = useQuery(GET_ALL_USERS, {
    errorPolicy: 'all'
  });

  const handleUserClick = (userId) => {
    navigate(`/admin/customer/edit?id=${userId}`);
  };

  const handleQuoteHistoryClick = (userId) => {
    navigate(`/admin/customer/quotations?id=${userId}`);
  };

  // 로딩 중일 때
  if (loading) {
    return (
      <div className="admin-main-content">
        <main className="admin-content">
          <h2>회원 정보 관리</h2>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <div>고객 정보를 불러오는 중입니다...</div>
          </div>
        </main>
      </div>
    );
  }

  // 에러 발생 시
  if (error) {
    return (
      <div className="admin-main-content">
        <main className="admin-content">
          <h2>회원 정보 관리</h2>
          <div style={{ textAlign: 'center', padding: '50px', color: '#ff4444' }}>
            <div>데이터를 불러오는 중 오류가 발생했습니다: {error.message}</div>
          </div>
        </main>
      </div>
    );
  }

  const users = data?.users || [];
  const activeUsers = users.filter(user => user.isActive);

  // 레벨 한글 변환 함수
  const getLevelText = (level) => {
    switch (level) {
      case 'A_GRADE': return 'A급';
      case 'B_GRADE': return 'B급';
      case 'C_GRADE': return 'C급';
      case 'D_GRADE': return 'D급';
      default: return 'D급'; // 기본값
    }
  };

  // 레벨별 색상 클래스 반환
  const getLevelClass = (level) => {
    switch (level) {
      case 'A_GRADE': return 'level-a';
      case 'B_GRADE': return 'level-b';
      case 'C_GRADE': return 'level-c';
      case 'D_GRADE': return 'level-d';
      default: return 'level-d';
    }
  };

  // 날짜 단축키 핸들러
  const handleDateShortcut = (months) => {
    const today = new Date();
    const pastDate = new Date(today);
    
    if (months === 'all') {
      // 전체 선택
      setDateRange({ start: '', end: '' });
    } else {
      pastDate.setMonth(pastDate.getMonth() - months);
      setDateRange({
        start: pastDate.toISOString().split('T')[0],
        end: today.toISOString().split('T')[0]
      });
    }
  };

  // 레벨 필터링 핸들러
  const handleLevelChange = (levelKey) => {
    if (levelKey === 'all') {
      setSelectedLevels({
        all: true,
        A_GRADE: false,
        B_GRADE: false,
        C_GRADE: false,
        D_GRADE: false
      });
    } else {
      setSelectedLevels(prev => {
        const newLevels = {
          ...prev,
          all: false,
          [levelKey]: !prev[levelKey]
        };
        
        // 모든 개별 레벨이 해제되면 전체를 체크
        const hasAnyLevelSelected = newLevels.A_GRADE || newLevels.B_GRADE || newLevels.C_GRADE || newLevels.D_GRADE;
        if (!hasAnyLevelSelected) {
          newLevels.all = true;
        }
        
        return newLevels;
      });
    }
  };

  // 검색 및 필터링
  const filteredUsers = activeUsers.filter(user => {
    // 검색어 필터링: 선택한 검색 타입(또는 전체)으로 검색
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase().trim();
      let matches = false;
      if (searchType === 'all') {
        matches =
          user.email?.toLowerCase().includes(searchLower) ||
          user.name?.toLowerCase().includes(searchLower) ||
          user.mobile?.toLowerCase().includes(searchLower) ||
          user.phone?.toLowerCase().includes(searchLower) ||
          user.company?.toLowerCase().includes(searchLower);
      } else {
        const field = { email: user.email, name: user.name, mobile: user.mobile, phone: user.phone, company: user.company }[searchType];
        matches = field?.toLowerCase().includes(searchLower) ?? false;
      }
      if (!matches) return false;
    }

    // 날짜 범위 필터링
    if (dateRange.start || dateRange.end) {
      const userDate = new Date(user.createdAt);
      const startDate = dateRange.start ? new Date(dateRange.start) : new Date('1900-01-01');
      const endDate = dateRange.end ? new Date(dateRange.end) : new Date('2100-12-31');
      
      // 종료일의 끝까지 포함
      endDate.setHours(23, 59, 59, 999);
      
      if (userDate < startDate || userDate > endDate) {
        return false;
      }
    }

    // 레벨 필터링
    if (!selectedLevels.all) {
      const userLevel = user.level || 'D_GRADE'; // 기본값 D급
      if (!selectedLevels[userLevel]) {
        return false;
      }
    }

    return true;
  });

  // 가입일 최신순 정렬 (서버 orderBy와 동일하게 프론트에서도 보장)
  const sortedFilteredUsers = [...filteredUsers].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );

  return (
    <>
      <div className="breadcrumb">
        <span>홈</span> &gt; <span>회원 정보 관리</span>
      </div>
      <div className="admin-main-content">
        <aside className="admin-sidebar">
          <div className="sidebar-menu">
            <h3>회원 정보 관리</h3>
            <ul>
              <li className="active" onClick={() => navigate('/admin/customer')}>회원 정보 관리</li>
              <li onClick={() => navigate('/admin/customer/register')}>회원 등록하기</li>
              <li onClick={() => navigate('/admin/customer/blocked')}>차단 회원 관리</li>
            </ul>
          </div>
          <div className="sidebar-menu">
            <h3>가입통계</h3>
            <ul>
              <li onClick={() => navigate('/admin/customer/stats')}>월별 가입 통계</li>
            </ul>
          </div>
        </aside>
        <main className="admin-content">
          <h2>회원 정보 관리</h2>
          <div className="search-filters">
            <div className="filter-group">
              <label>회원명</label>
              <div className="search-field">
                <select
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                >
                  <option value="all">전체</option>
                  <option value="email">아이디</option>
                  <option value="name">회원명</option>
                  <option value="mobile">핸드폰</option>
                  <option value="phone">연락처</option>
                  <option value="company">회사명</option>
                </select>
                <input 
                  type="text" 
                  placeholder="해당 정보를 기입하세요."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="filter-group">
              <label>기간 검색</label>
              <div className="date-range">
                <select>
                  <option>가입날짜</option>
                </select>
                <input 
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                />
                <span>~</span>
                <input 
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                />
                <button onClick={() => handleDateShortcut(1)}>1개월</button>
                <button onClick={() => handleDateShortcut(3)}>3개월</button>
                <button onClick={() => handleDateShortcut('all')}>전체</button>
              </div>
            </div>
            <div className="filter-group">
              <label>레벨 검색</label>
              <div className="level-options">
                <label>
                  <input 
                    type="checkbox" 
                    checked={selectedLevels.all}
                    onChange={() => handleLevelChange('all')}
                  /> 
                  전체
                </label>
                <label>
                  <input 
                    type="checkbox" 
                    checked={selectedLevels.A_GRADE}
                    onChange={() => handleLevelChange('A_GRADE')}
                  /> 
                  A급
                </label>
                <label>
                  <input 
                    type="checkbox" 
                    checked={selectedLevels.B_GRADE}
                    onChange={() => handleLevelChange('B_GRADE')}
                  /> 
                  B급
                </label>
                <label>
                  <input 
                    type="checkbox" 
                    checked={selectedLevels.C_GRADE}
                    onChange={() => handleLevelChange('C_GRADE')}
                  /> 
                  C급
                </label>
                <label>
                  <input 
                    type="checkbox" 
                    checked={selectedLevels.D_GRADE}
                    onChange={() => handleLevelChange('D_GRADE')}
                  /> 
                  D급
                </label>
              </div>
            </div>
          </div>
          <div className="search-section">
            <button className="search-button">검 색</button>
            <div className="filter-summary">
              필터 적용됨: 
              {!selectedLevels.all && (
                <span className="active-filters">
                  {selectedLevels.A_GRADE && <span className="filter-tag level-a">A급</span>}
                  {selectedLevels.B_GRADE && <span className="filter-tag level-b">B급</span>}
                  {selectedLevels.C_GRADE && <span className="filter-tag level-c">C급</span>}
                  {selectedLevels.D_GRADE && <span className="filter-tag level-d">D급</span>}
                </span>
              )}
              {searchTerm && <span className="filter-tag search">검색: "{searchTerm}"</span>}
              {(dateRange.start || dateRange.end) && <span className="filter-tag date">기간 필터</span>}
            </div>
          </div>

          <div className="user-list-container">
            <div className="user-list-header">
              <span>총 회원 수 : {sortedFilteredUsers.length} 명</span>
              <button className="add-user-button" onClick={() => navigate('/admin/customer/register')}>+ 회원 추가</button>
            </div>
            <table className="user-table">
              <thead>
                <tr>
                  <th>아이디</th>
                  <th>회사명</th>
                  <th>담당자</th>
                  <th>연락처</th>
                  <th>등급</th>
                  <th>로그인 횟수</th>
                  <th>견적 수</th>
                  <th>가입일시</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td data-label="아이디">{user.email}</td>
                    <td data-label="회사명">{user.company || '미입력'}</td>
                    <td data-label="담당자">{user.name}</td>
                    <td data-label="연락처">{user.phone || user.mobile || '미입력'}</td>
                    <td data-label="등급">
                      <span className={`level-badge ${getLevelClass(user.level)}`}>
                        {getLevelText(user.level)}
                      </span>
                    </td>
                    <td data-label="로그인 횟수">
                      {user.lastLoginAt ? '1+' : '0'}
                    </td>
                    <td data-label="견적 수">{user.quotations?.length || 0}</td>
                    <td data-label="가입일시">
                      {new Date(user.createdAt).toLocaleDateString('ko-KR')}
                    </td>
                    <td data-label="관리">
                      <button className="btn-edit" onClick={() => handleUserClick(user.id)}>수정</button>
                      <button className="btn-quote" onClick={() => handleQuoteHistoryClick(user.id)}>견적내역</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sortedFilteredUsers.length === 0 && (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <div>등록된 회원이 없습니다.</div>
              </div>
            )}
            <div className="pagination">
              <button>&lt;</button>
              <span>1 / 1</span>
              <button>&gt;</button>
            </div>
            {/* <div className="bottom-actions">
              <button>전체 메일 발송</button>
              <button>전체 문자 발송</button>
              <button>엑셀 저장</button>
            </div> */}
          </div>
        </main>
      </div>
    </>
  );
};

export default CustomerAdminPage; 